import {
  ChannelTypes,
  EventCategories,
  EventTypes,
  IEventChannel,
  ISubscription,
} from 'redux/reducers/subscriptions';
import { EntityTypes } from 'context/entityContext';
import { channelTypes } from '../typeToName';

/** It filters undefined and null values and valuable ones for countries and customers */
export const getValue = ({
  item,
  property,
}: {
  item: any;
  property: string;
}): any => {
  switch (item[property]) {
    case undefined:
    case null:
      return undefined;

    default:
      return item[property];
  }
};

/** Check if two subscriptions have the same country and customer */
export const compareCountriesDSPs = (
  first: ISubscription,
  second: ISubscription
): boolean =>
  getValue({ item: first, property: 'countryId' }) ===
    getValue({ item: second, property: 'countryId' }) &&
  getValue({ item: first, property: 'customerId' }) ===
    getValue({ item: second, property: 'customerId' });

/** Check if the channel has a category */
const checkCategoryInChannel =
  (initialCategory: any): any =>
  (currentChannel: IEventChannel): boolean =>
    currentChannel.eventCategories.includes(initialCategory);

/** Get priority playlisting event types */
const getPriorityPlaylistingEvents = (
  condition: boolean
): Record<string, unknown> | { eventTypes: EventTypes[] } =>
  condition
    ? {
        eventTypes: ['priority_playlist_add', 'priority_playlist_removal'],
      }
    : {};

/** Add priority playlisting to new subscriptions which should be created */
export const addPriorityPlaylisting = (subscriptions: ISubscription[]): any => {
  return subscriptions.map((subscr: ISubscription) => {
    const eventChannels = subscr.eventChannels.map((channel: IEventChannel) => {
      return {
        ...channel,
        ...(channel.eventCategories.includes('priority')
          ? {
              eventTypes: [
                'priority_playlist_add',
                'priority_playlist_removal',
              ],
              eventCategories: [
                ...new Set(
                  channel.eventCategories.filter(
                    (category: EventCategories) => category !== 'priority'
                  )
                ),
              ],
            }
          : {}),
      };
    });

    return {
      ...subscr,
      eventChannels,
    };
  });
};

/** Combine channels and save them */
export const combineChannels = ({
  categoryChannels,
  all,
  category,
  hasPriorityPlaylists,
}: {
  categoryChannels: IEventChannel[];
  all: any;
  category: EventCategories;
  hasPriorityPlaylists: boolean;
}): void => {
  categoryChannels.forEach((channel: IEventChannel) => {
    // find the channel, where we want to add a category
    let position;
    const found = all.find((currentChannel: IEventChannel, index: number) => {
      position = index;
      return channel.channelId === currentChannel.channelId;
    });

    const priorityPlaylisting = getPriorityPlaylistingEvents(
      hasPriorityPlaylists && category === 'playlisting'
    );

    // update the pushed earlier channel or create a new channel if we do not have pushed one
    if (found) {
      all.splice(position, 1, {
        channelId: channel.channelId,
        eventCategories: [...(found ? found.eventCategories : []), category],
        ...priorityPlaylisting,
      });
    } else {
      all.push({
        channelId: channel.channelId,
        eventCategories: [category],
        ...priorityPlaylisting,
      });
    }
  });
};

/** Get all channels where the category is listed */
export const getChannelsWithCategory = ({
  item,
  category,
}: {
  item: ISubscription;
  category: EventCategories;
}): IEventChannel[] =>
  item.eventChannels.filter(checkCategoryInChannel(category));

export const getAllCategoryChannels = ({
  channels,
  category,
  channel,
}: {
  channels: IEventChannel[];
  category: EventCategories;
  channel: IEventChannel;
}): any => {
  let allChannels: IEventChannel[] = channels;
  const hasNoSuchChannel = allChannels.every(
    (chan: IEventChannel) => chan.channelId !== channel.channelId
  );

  if (hasNoSuchChannel) {
    allChannels.push({
      channelId: channel.channelId,
      eventCategories: [category],
    });
  } else {
    allChannels = allChannels.map((chan: IEventChannel) => {
      return {
        ...chan,
        ...(chan.channelId === channel.channelId
          ? {
              eventCategories: [
                ...new Set([...chan.eventCategories, category]),
              ],
            }
          : {}),
      };
    });
  }

  return allChannels;
};

/** Construct new subscription */
export const constructNewSubscription = ({
  prev,
  countryId,
  customerId,
  channels,
  entityId,
  entityType,
}: {
  prev: ISubscription[];
  countryId: string;
  customerId: number;
  channels: IEventChannel[];
  entityId: string;
  entityType: EntityTypes;
}): any => {
  let position;
  /** Check if we already have the subscription in array of new subscriptions similar (if it has the same country and DSP) to the current one */
  const found = prev.find((subscription: ISubscription, index: number) => {
    const cond =
      subscription.countryId === countryId &&
      subscription.customerId === customerId;
    position = cond ? index : null;
    return cond;
  });

  /** If we have the similar one, we just update it with the corresponding channels and eventCategories */
  if (found) {
    const mergedChannels = Object.keys(channelTypes).reduce(
      (total: IEventChannel[], chan: ChannelTypes) => {
        const first = found.eventChannels.find(
          (ch: IEventChannel) => ch.channelId === chan
        );
        const second = channels.find(
          (ch: IEventChannel) => ch.channelId === chan
        );
        const merged = [
          ...new Set([
            ...(first && first.eventCategories.length > 0
              ? first?.eventCategories
              : []),
            ...(second && second.eventCategories.length > 0
              ? second?.eventCategories
              : []),
          ]),
        ];

        if (merged.length > 0) {
          total.push({
            channelId: chan,
            eventCategories: merged,
          });
        }
        return total;
      },
      []
    );

    prev.splice(position as any, 1, {
      ...found,
      eventChannels: mergedChannels,
    });
  } else {
    /** If we do not, just push a new subscription to the array of subscriptions to save */
    prev.push({
      countryId,
      customerId,
      eventChannels: channels,
      entityId,
      entityType,
    });
  }
};

export const parsePriorityTypes = (
  subscription: ISubscription
): IEventChannel[] =>
  subscription.eventChannels.map((channel: IEventChannel) =>
    channel.hasOwnProperty('eventTypes') &&
    !channel?.eventCategories?.includes('playlisting')
      ? {
          ...channel,
          eventCategories: [].concat(
            [],
            channel?.eventCategories ? channel.eventCategories : ([] as any),
            ['priority'] as any
          ),
        }
      : channel
  );

export const checkCondition = ({ item, hasCategory }: any): boolean => {
  return hasCategory
    ? item?.eventCategories?.includes('priority')
    : item?.hasOwnProperty('eventTypes') &&
        (item?.eventTypes?.includes('priority_playlist_add') ||
          item?.eventTypes.includes('priority_playlist_removal'));
};

export const addPriorityWeeklyEmail = ({
  channels,
  hasCategory = false,
}: any): any => {
  const hasPriority = channels.find((channel: any) =>
    checkCondition({ item: channel, hasCategory })
  );
  const hasPriorityWeeklyChannel = channels.find((channel: any) =>
    channel?.channelId.includes('email_weekly_digest')
  );
  const hasPriorityDailyChannel = channels.find((channel: any) =>
    channel?.channelId.includes('email_daily_digest')
  );

  if (hasPriority && hasPriorityDailyChannel) {
    const newChannel = {
      channelId: 'email_weekly_digest',
      ...(hasCategory
        ? { eventCategories: ['priority'] }
        : {
            eventTypes: ['priority_playlist_add', 'priority_playlist_removal'],
          }),
    };

    return !hasPriorityWeeklyChannel
      ? [...channels, newChannel]
      : channels.map((channel: any) => {
          return channel.channelId === 'email_weekly_digest'
            ? {
                channelId: channel?.channelId,
                eventCategories: channel.eventCategories,
                eventTypes: [
                  'priority_playlist_add',
                  'priority_playlist_removal',
                ],
              }
            : channel;
        });
  }
  return channels;
};
